import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Divider } from '@/components/Divider/Divider.component'
import { Button } from '@/components/ui/button'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'

import { BehaviorDetectionSheet } from './Components/BehaviorDetectionSheet'
import { DefaultBehaviorFlags } from './Components/DefaultBehaviorFlags'
import { useUserInfoQuery } from '@/store/api/Users/Users.query'
import { useActivateAccountMutation } from '@/store/api/Auth/Auth.query'
import { toast } from 'sonner'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useEffect, useRef } from 'react'
import { EPlanStatus } from '@/store/api/Stripe/Stripe.types'
import { trackOnboardingStepViewed, OnboardingSteps } from '@/utils/analytics'

export const DefaultBehaviorAndTopics = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const hasTrackedView = useRef(false)

  const { data: userData, isError, isLoading, isSuccess } = useUserInfoQuery()
  const [activateAccount, { isLoading: isActivateAccountLoading }] =
    useActivateAccountMutation()

  // Track step viewed once on mount
  useEffect(() => {
    if (!hasTrackedView.current) {
      trackOnboardingStepViewed(OnboardingSteps.BEHAVIOR_DETECTION)
      hasTrackedView.current = true
    }
  }, [])

  const handleActivateAccount = async () => {
    const userID = userData?.id
    if (!userID) {
      return
    }
    try {
      await activateAccount({ userId: userID }).unwrap()
    } catch (error) {
      toast.error(t('setup.focusedTopics.errorActivating'))
      catchWithSentry(error)
    }
  }

  useEffect(() => {
    if (userData) {
      handleActivateAccount()
    }
  }, [isSuccess])

  const handleLaunchButton = () => {
    // if the user has an active plan or the user has one time credit purchase
    if (
      userData?.account.plan?.status === EPlanStatus.ACTIVE ||
      userData?.account.credits !== 0
    ) {
      navigate(AppRoutes.dashboard)
    } else {
      navigate(AppRoutes.generatingReportMock)
    }
  }

  return (
    <WeScreen
      useNextButton={false}
      useHeader={false}
      customViewStyles="max-md:p-0"
      isLoading={isActivateAccountLoading || isLoading}
      isError={isError}
    >
      <div className="flex flex-col justify-center w-full items-center h-full">
        <div className="flex flex-col w-1/2 max-lg:w-full h-min max-lg:h-full bg-baseColorHighlight rounded-2xl p-6 max-sm:p-3 gap-6">
          <div className="flex flex-col gap-3">
            <p className="text-neutralColor text-xl font-medium">
              {t('setup.behaviorDetection.defaultBehaviorFlagsTitle')}
            </p>
            <BehaviorDetectionSheet />
            <p className="text-neutralColor80 text-base font-normal">
              {t('setup.behaviorDetection.defaultBehaviorFlagsDescription1')}
              <span className="font-bold">
                {t('setup.behaviorDetection.defaultBehaviorFlagsDescription2')}
              </span>
              {t('setup.behaviorDetection.defaultBehaviorFlagsDescription3')}
              <span className="font-bold">
                {t('setup.behaviorDetection.defaultBehaviorFlagsDescription4')}
              </span>
              {t('setup.behaviorDetection.defaultBehaviorFlagsDescription5')}
              <span className="font-bold">
                {t('setup.behaviorDetection.defaultBehaviorFlagsDescription6')}
              </span>
            </p>
          </div>
          <DefaultBehaviorFlags />
          <Divider />
          <div className="flex flex-col gap-3">
            <p className="text-neutralColor text-xl font-medium">
              {t('setup.focusedTopics.comingSoonTitle')}
            </p>
            <p className="text-neutralColor80 text-base font-normal">
              {t('setup.focusedTopics.comingSoonDescription')}
            </p>
          </div>
          <Divider />
          <div className="flex justify-end">
            <Button
              className="font-medium text-base "
              onClick={handleLaunchButton}
              isLoading={isActivateAccountLoading}
            >
              {t('setup.focusedTopics.launchButton')}
            </Button>
          </div>
        </div>
      </div>
    </WeScreen>
  )
}
